import { z } from "zod"
import { parseJson } from "@common/utils/parseJson"

const LEDGER_STORAGE_KEY = "dioptra.scroll-box-reports"
const REPORT_MEMORY_MS = 7 * 24 * 60 * 60 * 1000
const MAXIMUM_REMEMBERED_REPORTS = 200

const RememberedReportSchema = z.object({ reportKey: z.string(), reportedAtMs: z.number() })
const RememberedReportsSchema = z.array(RememberedReportSchema)

type RememberedReport = z.infer<typeof RememberedReportSchema>

function readRememberedReports(storage: Storage): RememberedReport[] {
  const stored = storage.getItem(LEDGER_STORAGE_KEY)
  if (stored === null) return []
  const parsed = RememberedReportsSchema.safeParse(parseJson(stored))
  return parsed.success ? parsed.data : []
}

function claimInStorage(storage: Storage, reportKey: string, nowMs: number): boolean {
  const recentReports = readRememberedReports(storage).filter(
    (remembered) => nowMs - remembered.reportedAtMs < REPORT_MEMORY_MS,
  )
  if (recentReports.some((remembered) => remembered.reportKey === reportKey)) return false
  const rememberedReports = [...recentReports, { reportKey, reportedAtMs: nowMs }].slice(-MAXIMUM_REMEMBERED_REPORTS)
  storage.setItem(LEDGER_STORAGE_KEY, JSON.stringify(rememberedReports))
  return true
}

export function claimScrollBoxReport(storage: Storage, reportKey: string, nowMs: number): boolean {
  try {
    return claimInStorage(storage, reportKey, nowMs)
  } catch (error) {
    if (error instanceof DOMException) return false
    throw error
  }
}

export function persistentReportStore(): Storage | null {
  try {
    return window.localStorage
  } catch (error) {
    if (error instanceof DOMException) return null
    throw error
  }
}
